/* Bind original founder/shop affordances to immutable, silent, theme-owned films. */
(() => {
  if (!window.seifeRealVideoSlots) {
    let active = null;
    const close = () => {
      if (!active) return;
      const { video, modal, trigger, cleanup, overflowTarget, previousOverflow } = active;
      active = null;
      video.pause();
      video.removeAttribute('src');
      video.load();
      cleanup();
      overflowTarget.style.overflow = previousOverflow;
      if (trigger.isConnected) trigger.focus({ preventScroll: true });
      modal.removeAttribute('data-real-playing');
    };
    const open = trigger => {
      close();
      const owner = trigger.closest('[data-real-founder], [data-llt]');
      if (!owner) return;
      const isFounder = owner.hasAttribute('data-real-founder');
      let modal, wrapper, dialog, closeButton, cleanup;
      if (isFounder) {
        // Existing founder modal shell; owned frame fits the film's portrait ratio.
        modal = document.createElement('div');
        modal.className = 'founder-video-modal';
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');
        const backdrop = document.createElement('div');
        backdrop.className = 'founder-video-modal-backdrop';
        backdrop.addEventListener('click', close);
        dialog = document.createElement('div');
        dialog.className = 'founder-video-modal-content';
        const heading = document.createElement('h2');
        heading.className = 'visually-hidden';
        heading.id = `RealFounderTitle-${owner.dataset.ownerId}`;
        heading.textContent = trigger.dataset.realTitle;
        modal.setAttribute('aria-labelledby', heading.id);
        closeButton = document.createElement('button');
        closeButton.type = 'button';
        closeButton.className = 'founder-video-modal-close';
        closeButton.setAttribute('aria-label', owner.dataset.closeLabel);
        closeButton.textContent = '\u00d7';
        closeButton.addEventListener('click', close);
        wrapper = document.createElement('div');
        wrapper.className = 'founder-video-modal-wrapper';
        dialog.append(heading, closeButton, wrapper);
        modal.append(backdrop, dialog);
        document.body.append(modal);
        cleanup = () => modal.remove();
      } else {
        // Use the already-rendered shop dialog and its approved title/close controls.
        modal = owner.querySelector('[data-llt-modal]');
        dialog = modal.querySelector('[role="dialog"]');
        wrapper = modal.querySelector('[data-llt-modal-content]');
        closeButton = modal.querySelector('button[data-llt-close]');
        modal.querySelector('[data-llt-modal-title]').textContent = trigger.dataset.realTitle;
        wrapper.replaceChildren();
        modal.hidden = false;
        cleanup = () => { modal.hidden = true; wrapper.replaceChildren(); };
      }
      const video = document.createElement('video');
      video.dataset.realVideo = trigger.dataset.realSlot;
      video.controls = true;
      video.playsInline = true;
      video.muted = true;
      video.preload = 'none';
      video.poster = trigger.dataset.realPoster;
      video.setAttribute('aria-label', trigger.dataset.realTitle);
      video.setAttribute('tabindex', '0');
      const source = () => video.canPlayType('video/mp4; codecs="avc1.64001f"') ? trigger.dataset.realMp4 : trigger.dataset.realWebm;
      const error = document.createElement('div');
      error.className = 'real-video-error';
      error.hidden = true;
      error.setAttribute('role', 'alert');
      const message = document.createElement('p');
      message.textContent = trigger.dataset.realError;
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.textContent = trigger.dataset.realRetry;
      error.append(message, retry);
      wrapper.append(video, error);
      const overflowTarget = isFounder ? document.body : document.documentElement;
      const previousOverflow = overflowTarget.style.overflow;
      overflowTarget.style.overflow = 'hidden';
      active = { video, modal, trigger, cleanup, overflowTarget, previousOverflow };
      modal.setAttribute('data-real-playing', trigger.dataset.realSlot);
      const fitPortrait = () => {
        if (active?.video !== video || document.fullscreenElement === video) return;
        const viewport = window.visualViewport;
        const height = viewport?.height || window.innerHeight;
        const width = viewport?.width || window.innerWidth;
        const top = viewport?.offsetTop || 0;
        const draftPreview = window.Shopify?.theme?.role === 'unpublished' || new URLSearchParams(location.search).has('preview_theme_id');
        // The preview toolbar can live in closed shadow DOM, hiding its rectangle.
        // Reserve its safe footprint only in draft preview; public playback uses it all.
        let coveredBottom = draftPreview ? 64 : 0;
        // Shopify's draft bar is an overlay, not part of the usable playback area.
        document.querySelectorAll('iframe,[id*="preview-bar"],[id*="PreviewBar"]').forEach(element => {
          const rect = element.getBoundingClientRect();
          if (rect.width > width * .5 && rect.height > 0 && rect.height < 120 && rect.bottom >= top + height - 2 && rect.top > top + height * .75) coveredBottom = Math.max(coveredBottom, top + height - rect.top);
        });
        const usableHeight = Math.max(1, height - coveredBottom);
        modal.style.setProperty('--real-viewport-top', `${top}px`);
        modal.style.setProperty('--real-viewport-height', `${usableHeight}px`);
        const padding = getComputedStyle(dialog);
        const horizontal = parseFloat(padding.paddingLeft) + parseFloat(padding.paddingRight);
        const vertical = parseFloat(padding.paddingTop) + parseFloat(padding.paddingBottom);
        const heading = dialog.querySelector('h2');
        const ratio = (video.videoWidth || 720) / (video.videoHeight || 1280);
        const maxFrameWidth = Math.max(1, Math.min(720, width - 32 - horizontal));
        const minimumDialogWidth = Math.min(300, width - 32);
        let frameWidth = maxFrameWidth;
        dialog.style.width = `${frameWidth + horizontal}px`;
        for (let pass = 0; pass < 5; pass++) {
          const headingStyle = getComputedStyle(heading);
          const chrome = vertical + heading.getBoundingClientRect().height + parseFloat(headingStyle.marginTop) + parseFloat(headingStyle.marginBottom);
          frameWidth = Math.min(frameWidth, Math.max(1, usableHeight - 32 - chrome) * ratio);
          dialog.style.width = `${Math.max(minimumDialogWidth, frameWidth + horizontal)}px`;
        }
        wrapper.style.width = `${frameWidth}px`;
        wrapper.style.height = `${frameWidth / ratio}px`;
      };
      const originalCleanup = cleanup;
      active.cleanup = () => {
        window.removeEventListener('resize', fitPortrait);
        window.visualViewport?.removeEventListener('resize', fitPortrait);
        window.visualViewport?.removeEventListener('scroll', fitPortrait);
        originalCleanup();
      };
      window.addEventListener('resize', fitPortrait);
      window.visualViewport?.addEventListener('resize', fitPortrait);
      window.visualViewport?.addEventListener('scroll', fitPortrait);
      video.addEventListener('loadedmetadata', fitPortrait);
      document.fonts?.ready.then(() => { if (active?.video === video) fitPortrait(); });
      fitPortrait();
      const play = async () => {
        error.hidden = true;
        video.src = source();
        try { await video.play(); }
        catch (e) { if (active?.video === video && e.name !== 'AbortError') error.hidden = false; }
      };
      video.addEventListener('error', () => { if (active?.video === video) error.hidden = false; });
      retry.addEventListener('click', play);
      video.addEventListener('play', () => {
        document.querySelectorAll('video[data-real-video]').forEach(other => { if (other !== video) other.pause(); });
      });
      (isFounder ? closeButton : dialog).focus({ preventScroll: true });
      play(); // Directly initiated by the original play-card/chip click; no autoplay attribute.
    };
    const init = () => {
      document.querySelectorAll('[data-real-slot]').forEach(button => {
        if (button.dataset.realBound) return;
        button.dataset.realBound = 'true';
        button.addEventListener('click', () => open(button));
      });
      document.querySelectorAll('[data-llt-modal] [data-llt-close]').forEach(button => {
        if (button.dataset.realCloseBound) return;
        button.dataset.realCloseBound = 'true';
        button.addEventListener('click', () => { if (active?.modal.contains(button)) close(); });
      });
    };
    // Let Tab traverse native shadow-DOM video controls instead of treating the video
    // host as the last control on every keystroke. The original shop trap is bubbling.
    document.addEventListener('keydown', event => {
      if (active && event.key === 'Tab' && document.activeElement === active.video) event.stopPropagation();
    }, true);
    document.addEventListener('focusin', event => {
      if (active && !active.modal.contains(event.target)) active.modal.querySelector('button')?.focus();
    });
    document.addEventListener('keydown', event => {
      if (!active) return;
      if (event.key === 'Escape') { close(); return; }
      if (event.key !== 'Tab' || event.defaultPrevented) return;
      const controls = [...active.modal.querySelectorAll('button,video,[tabindex]')].filter(el => !el.closest('[hidden]'));
      const first = controls[0], last = controls.at(-1);
      if (event.shiftKey && (document.activeElement === first || !controls.includes(document.activeElement))) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    });
    document.addEventListener('visibilitychange', () => { if (document.hidden) active?.video.pause(); });
    window.addEventListener('pagehide', close);
    document.addEventListener('shopify:section:unload', event => { if (active && event.target.contains(active.trigger)) close(); });
    document.addEventListener('shopify:section:load', init);
    window.seifeRealVideoSlots = { init };
  }
  window.seifeRealVideoSlots.init();
})();
